import { inject, Injectable, OnDestroy } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Events } from '@df/core/service/event';
import { Status } from '@df/core/status';
import { isPwaStandalonForced } from '@df/module-pwa/pwa-const';
import { TimeoutService } from '@df/ng/timeout.service';
import { CustomerModel } from '@df/session/api/customer.model';
import { Customer } from '@df/session/customer/customer.interfaces';
import { BehaviorSubject, Subscription } from 'rxjs';
import { NMyOutfits } from '../account/pwa/my-outfits/my-outfits.interfaces';
import { NProfiles } from '../account/pwa/profiles/profile.interfaces';
import { NAntavo } from '../module/antavo/antavo.interface';
import { AntavoService } from '../module/antavo/antavo.service';
import { UserImageRole, UserImagesService } from './user-images.service';
import { NgInject } from '@df/ng/ng-inject';
import { isPwaStandalone } from '@df/module-pwa/pwa-const';

declare module '@df/session/customer/customer.interfaces' {
    export namespace Customer {
        export interface ICustomerData {
            accepted_loyalty?: boolean;
            customer_profile?: string;
            loyalty_id?: string;
            saved_outfits?: string; // JSON string of saved outfits data
            spendable_points?: number;
            nickname?: string;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class LocalCustomerModel extends CustomerModel implements OnDestroy {
    private antavoService = inject(AntavoService);
    private timeoutService = inject(TimeoutService);
    private userImagesService = inject(UserImagesService);
    private events: Events | undefined = inject(Events);
    private checkoutSuccessSubscription: Subscription | undefined;
    private eventListenerInitialized = false;
    private readonly ANTAVO_REFRESH_THROTTLE_MS = 60000; // 1 minute
    readonly antavoCustomerData: BehaviorSubject<NAntavo.ICustomerResponse | undefined> = new BehaviorSubject<
        NAntavo.ICustomerResponse | undefined
    >(undefined);

    /**
     * Throttled version of refreshAntavoMemberStatus
     * Max one call per minute
     */
    readonly refreshAntavoMemberStatusThrottled = this.timeoutService.throttle(
        (data: Customer.ICustomerData = this.data) => this.refreshAntavoMemberStatus(data),
        this.ANTAVO_REFRESH_THROTTLE_MS,
        { leading: true, trailing: false }
    );

    ngOnDestroy(): void {
        this.checkoutSuccessSubscription?.unsubscribe();
    }

    /**
     * Sets up listener for checkout success to refresh Antavo data (lazy initialization)
     */
    private ensureEventListenersInitialized(): void {
        if (this.eventListenerInitialized || !isPwaStandalonForced) {
            return;
        }

        this.eventListenerInitialized = true;
        this.checkoutSuccessSubscription = this.events?.on(Checkout.EVENTS.SuccessTracking).subscribe(() => {
            this.refreshAntavoMemberStatusThrottled();
        });
    }

    override async setData(data: Customer.ICustomerData | Customer.ILoggedoutData): Promise<void> {
        const customerData = data as Customer.ICustomerData;

        // Ensure event listeners are initialized
        this.ensureEventListenersInitialized();

        if (isPwaStandalonForced) {
            // Optional cause user notifications service calling before initializing
            if (customerData.loyalty_id) {
                // Refresh immediately if loyalty_id changed
                if (customerData.loyalty_id !== this.antavoCustomerData?.value?.id) {
                    this.refreshAntavoMemberStatusThrottled(customerData);
                    this.userImagesService.loadUserImages(customerData.loyalty_id);
                }
            } else if (customerData && !customerData.loyalty_id) {
                // Clear user images cache
                // can be undefined
                this.userImagesService?.clearCache();
            }
        }

        return super.setData(data);
    }

    get antavoTierData(): NAntavo.ITier | undefined {
        return this.antavoCustomerData.value?.tiers?.[0];
    }

    get memberStatusTitle(): string | undefined {
        return this.antavoTierData?.name;
    }

    get rosettes(): number {
        // Use tier account earned points if available
        const tierAccount = this.antavoCustomerData.value?.accounts?.find(account => account.id === 'tier');
        if (tierAccount) {
            return tierAccount.earned;
        }
        return 0;
    }

    get pointsToNextTier(): number {
        // Calculate points needed to reach next tier
        if (this.antavoTierData?.next) {
            return this.totalPointsForNextTier - this.rosettes;
        }
        return 0;
    }

    get totalPointsForNextTier(): number {
        if (this.antavoTierData?.next) {
            return this.antavoTierData.points + (this.antavoTierData.next.points || 0);
        }
        return 0;
    }

    get nextTierName(): string {
        if (this.antavoTierData?.next) {
            return this.antavoTierData.next.name;
        }
        return '';
    }

    async refreshAntavoMemberStatus(data: Customer.ICustomerData): Promise<NAntavo.ICustomerResponse | undefined> {
        if (data.loyalty_id) {
            const antavoCustomerData = await this.antavoService.loadCustomer({
                loyaltyId: data.loyalty_id
            });
            this.antavoCustomerData.next(antavoCustomerData);
            return antavoCustomerData;
        }
    }

    get coins(): number {
        return this.data.spendable_points || 0;
    }

    get isLoyaltyTermsAccepted(): boolean {
        return !!this.data.accepted_loyalty;
    }

    get loyaltyId(): string | undefined {
        return this.data?.loyalty_id;
    }

    get customerProfile(): NProfiles.IProfileData {
        try {
            return JSON.parse(this.data.customer_profile || '{}');
        } catch (error) {
            console.error('Error parsing customer profile', error);
            return {};
        }
    }

    get savedOutfits(): NMyOutfits.ISavedOutfit[] {
        try {
            return JSON.parse(this.data.saved_outfits || '{}').outfits || [];
        } catch (error) {
            console.error('Error parsing customer profile', error);
            return [];
        }
    }

    /**
     * Saves profile image to the server
     */
    async saveUserImage(imageRole: UserImageRole, file: File, status?: Status, index?: number): Promise<void> {
        if (!this.loyaltyId) {
            throw new Error('Loyalty ID is required to save profile image');
        }

        // Delegate to user images service
        await this.userImagesService.saveUserImage(this.loyaltyId, imageRole, file, status, index);
    }

    /**
     * Gets the profile image path
     */
    get profileImage(): string | undefined {
        return this.userImagesService.getUserImage(UserImageRole.Profile);
    }

    getUserImage(role: UserImageRole, index?: number): string | undefined {
        return this.userImagesService.getUserImage(role, index);
    }
}
